/**
 * Quantity Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  GradientDirection,
  SpacingInputValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseColorFormat,
  parseSpacingFormat,
  parseToVariableText,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatColorFormat,
  formatSpacingFormat,
} from '../../format-utils'
import quantityTemplate from './quantity.template.json'

export interface IQuantityComponentSettings {
  // Data settings
  'quantity-data-label': VariableTextValue
  'quantity-data-min': number
  'quantity-data-max': number

  // Design settings
  'quantity-design-button-color': string
  'quantity-design-button-color-solid': string
  'quantity-design-button-color-start-gradient': string
  'quantity-design-button-color-end-gradient': string
  'quantity-design-button-color-gradient-direction': string
  'quantity-design-input-color': string
  'quantity-design-input-color-solid': string
  'quantity-design-margin': SpacingInputValue

  // Actions
  'quantity-action-on-increase': BuilderActions
  'quantity-action-on-decrease': BuilderActions
}

export interface IQuantityComponent extends IBuilderComponent {
  componentId: 'quantity'
  groupId: 'app'
  settings: IQuantityComponentSettings
}

/**
 * Quantity AI Input
 */
export interface AiQuantityInput {
  dataSource?: string // Table ID
  valueColumn?: string // Column ID for the value
  label?: string[] // Format: ["text:Quantity"] or ["text:Items for ", "cr:product"]
  min?: number // Minimum value
  max?: number // Maximum value
  margin?: string // Format: "m:8px"
  buttonColor?: string // Format: "solid:primary-40" or "adaptive"
  inputColor?: string // Format: "solid:primary-40" or "adaptive"
  actionOnIncrease?: BuilderActions
  actionOnDecrease?: BuilderActions
}

const QUANTITY_DEFAULT_TEMPLATE = quantityTemplate as unknown as IQuantityComponentSettings

export class QuantityComp {
  getTemplate(): IQuantityComponentSettings {
    return QUANTITY_DEFAULT_TEMPLATE
  }

  /**
   * format quantity component settings to AI quantity input
   */
  format(settings: IQuantityComponentSettings): AiQuantityInput {
    const result: AiQuantityInput = {}

    // Data - Label
    if (settings['quantity-data-label']) {
      result.label = formatFromVariableText(settings['quantity-data-label'])
    }

    // Data - Min
    if (settings['quantity-data-min'] !== undefined) {
      result.min = settings['quantity-data-min']
    }

    // Data - Max
    if (settings['quantity-data-max'] !== undefined) {
      result.max = settings['quantity-data-max']
    }

    // Design - Margin
    if (settings['quantity-design-margin']) {
      result.margin = formatSpacingFormat({ phone: settings['quantity-design-margin'] } as any)
    }

    // Design - Button Color
    if (settings['quantity-design-button-color']) {
      result.buttonColor = formatColorFormat({
        colorType: { light: settings['quantity-design-button-color'] as any, dark: settings['quantity-design-button-color'] as any },
        colorSolid: { light: settings['quantity-design-button-color-solid'] as any, dark: settings['quantity-design-button-color-solid'] as any },
        colorStartGradient: { light: settings['quantity-design-button-color-start-gradient'] as any, dark: settings['quantity-design-button-color-start-gradient'] as any },
        colorEndGradient: { light: settings['quantity-design-button-color-end-gradient'] as any, dark: settings['quantity-design-button-color-end-gradient'] as any },
        colorGradientDirection: { light: settings['quantity-design-button-color-gradient-direction'] as any, dark: settings['quantity-design-button-color-gradient-direction'] as any },
      })
    }

    // Design - Input Color
    if (settings['quantity-design-input-color']) {
      result.inputColor = formatColorFormat({
        colorType: { light: settings['quantity-design-input-color'] as any, dark: settings['quantity-design-input-color'] as any },
        colorSolid: { light: settings['quantity-design-input-color-solid'] as any, dark: settings['quantity-design-input-color-solid'] as any },
      })
    }

    return result
  }

  /**
   * parse AI quantity input to quantity component settings
   */
  parse(input: AiQuantityInput): IQuantityComponentSettings {
    const result = deepMerge(QUANTITY_DEFAULT_TEMPLATE, {})

    // Data - Label
    if (input.label) {
      result['quantity-data-label'] = parseToVariableText(input.label)
    }

    // Data - Min
    if (input.min !== undefined) {
      result['quantity-data-min'] = input.min
    }

    // Data - Max
    if (input.max !== undefined) {
      result['quantity-data-max'] = input.max
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['quantity-design-margin'] = marginParsed.phone
    }

    // Design - Button Color (simple string format in template)
    if (input.buttonColor) {
      // Parse color format and extract values
      const colorParsed = parseColorFormat(input.buttonColor)
      result['quantity-design-button-color'] = colorParsed.colorType.light
      result['quantity-design-button-color-solid'] = colorParsed.colorSolid.light
      result['quantity-design-button-color-start-gradient'] = colorParsed.colorStartGradient.light
      result['quantity-design-button-color-end-gradient'] = colorParsed.colorEndGradient.light
      result['quantity-design-button-color-gradient-direction'] = colorParsed.colorGradientDirection.light
    }

    // Design - Input Color (simple string format in template)
    if (input.inputColor) {
      const colorParsed = parseColorFormat(input.inputColor)
      result['quantity-design-input-color'] = colorParsed.colorType.light
      result['quantity-design-input-color-solid'] = colorParsed.colorSolid.light
    }

    // Actions
    if (input.actionOnIncrease) {
      result['quantity-action-on-increase'] = input.actionOnIncrease
    }

    if (input.actionOnDecrease) {
      result['quantity-action-on-decrease'] = input.actionOnDecrease
    }

    return result
  }
}

